/**
 * Demo Query-Response Database
 * Used for the landing page demo portal to simulate the actual platform
 * Each entry has a query pattern and corresponding HTML-formatted response
 */

export const demoResponses = [
  // ========== DISK PARTITIONS ==========
  {
    patterns: ["disk partition", "partitions", "disk information", "disk0", "disk3"],
    query: "can you tell me what disk partitions are there in this machine",
    response: `<h2>Disk Partitions Overview</h2>

<h3>Internal physical disk: disk0</h3>
<ul>
  <li><strong>GUID_partition_scheme</strong> — 1.0 TB • disk0</li>
  <li><strong>Apple_APFS_ISC Container</strong> — 524.3 MB • disk0s1</li>
  <li><strong>Apple_APFS Container</strong> — 994.7 GB • disk0s2</li>
  <li><strong>Apple_APFS_Recovery Container</strong> — 5.4 GB • disk0s3</li>
</ul>

<h3>Synthesized disk: disk3</h3>
<ul>
  <li><strong>APFS Container Scheme</strong> — +994.7 GB • disk3 (Physical Store: disk0s2)</li>
  <li><strong>APFS Volume Macintosh HD</strong> — 11.3 GB • disk3s1</li>
  <li><strong>APFS Snapshot com.apple.os.update-...</strong> — 11.3 GB • disk3s1s1</li>
  <li><strong>APFS Volume Preboot</strong> — 7.1 GB • disk3s2</li>
  <li><strong>APFS Volume Recovery</strong> — 1.0 GB • disk3s3</li>
  <li><strong>APFS Volume Data</strong> — 442.9 GB • disk3s5</li>
  <li><strong>APFS Volume VM</strong> — 20.5 KB • disk3s6</li>
</ul>

<p><em>Note: No errors detected; command completed with return code 0.</em></p>`
  },

  // ========== RUNNING PROCESSES ==========
  {
    patterns: ["running process", "list process", "all process", "top process", "memory usage"],
    query: "list all running processes on this machine",
    response: `<h2>Running Processes</h2>

<table>
  <thead>
    <tr>
      <th>PID</th>
      <th>Process Name</th>
      <th>CPU %</th>
      <th>Memory</th>
      <th>User</th>
      <th>Status</th>
    </tr>
  </thead>
  <tbody>
    <tr><td>1</td><td>launchd</td><td>0.1%</td><td>45.2 MB</td><td>root</td><td>Running</td></tr>
    <tr><td>234</td><td>WindowServer</td><td>8.5%</td><td>892.4 MB</td><td>root</td><td>Running</td></tr>
    <tr><td>456</td><td>Finder</td><td>1.2%</td><td>156.8 MB</td><td>user</td><td>Running</td></tr>
    <tr><td>789</td><td>Chrome</td><td>15.3%</td><td>2.4 GB</td><td>user</td><td>Running</td></tr>
    <tr><td>1012</td><td>python3</td><td>5.7%</td><td>512.3 MB</td><td>user</td><td>Running</td></tr>
    <tr><td>1345</td><td>Docker Desktop</td><td>3.2%</td><td>1.8 GB</td><td>user</td><td>Running</td></tr>
    <tr><td>1678</td><td>Slack</td><td>2.1%</td><td>345.6 MB</td><td>user</td><td>Running</td></tr>
    <tr><td>2001</td><td>VS Code</td><td>4.8%</td><td>768.2 MB</td><td>user</td><td>Running</td></tr>
  </tbody>
</table>

<p><strong>Total Processes:</strong> 342 &nbsp;&nbsp;|&nbsp;&nbsp; <strong>Running:</strong> 287 &nbsp;&nbsp;|&nbsp;&nbsp; <strong>Sleeping:</strong> 55</p>`
  },

  // ========== PE FILE ANALYSIS ==========
  {
    patterns: ["pe file", ".init section", "pe header", "sections", "imports"],
    query: "find all PE files with specific import 'CreateRemoteThread' from kernel32.dll in current directory",
    response: `<h2>PE File Scan Results</h2>

<p>Scanning directory for PE files with <code>CreateRemoteThread</code> import from <code>kernel32.dll</code>...</p>

<h3>Files Found: 3</h3>

<table>
  <thead>
    <tr>
      <th>File Name</th>
      <th>Size</th>
      <th>Architecture</th>
      <th>Suspicious Imports</th>
    </tr>
  </thead>
  <tbody>
    <tr>
      <td>🔴 malware_sample.exe</td>
      <td>156 KB</td>
      <td>PE32 (x86)</td>
      <td>CreateRemoteThread, VirtualAllocEx, WriteProcessMemory</td>
    </tr>
    <tr>
      <td>🟡 debug_injector.exe</td>
      <td>89 KB</td>
      <td>PE32+ (x64)</td>
      <td>CreateRemoteThread, NtQueryInformationProcess</td>
    </tr>
    <tr>
      <td>🟢 legitimate_tool.exe</td>
      <td>234 KB</td>
      <td>PE32 (x86)</td>
      <td>CreateRemoteThread (used for debugging)</td>
    </tr>
  </tbody>
</table>

<p><em>⚠️ Warning: Files with CreateRemoteThread import may indicate process injection capabilities.</em></p>`
  },

  // ========== VIRUSTOTAL SCAN ==========
  {
    patterns: ["virustotal", "vt scan", "hash scan", "malware scan", "detection ratio"],
    query: "scan this file hash on VirusTotal: 44d88612fea8a8f36de82e1278abb02f",
    response: `<h2>VirusTotal Analysis Report</h2>

<h3>File Hash: <code>44d88612fea8a8f36de82e1278abb02f</code></h3>

<div style="background: rgba(244, 67, 54, 0.1); border: 1px solid rgba(244, 67, 54, 0.3); border-radius: 8px; padding: 12px; margin: 12px 0;">
  <strong style="color: #ff6b6b;">⚠️ MALICIOUS</strong> - Detection Ratio: <strong>58/72</strong>
</div>

<table>
  <thead>
    <tr>
      <th>Engine</th>
      <th>Detection</th>
      <th>Result</th>
    </tr>
  </thead>
  <tbody>
    <tr><td>Kaspersky</td><td style="color: #ff6b6b;">🔴 Detected</td><td>Trojan.Win32.Eicar</td></tr>
    <tr><td>Microsoft</td><td style="color: #ff6b6b;">🔴 Detected</td><td>Virus:DOS/EICAR_Test_File</td></tr>
    <tr><td>Avast</td><td style="color: #ff6b6b;">🔴 Detected</td><td>EICAR Test-NOT virus</td></tr>
    <tr><td>BitDefender</td><td style="color: #ff6b6b;">🔴 Detected</td><td>EICAR-Test-File (not a virus)</td></tr>
    <tr><td>Norton</td><td style="color: #ff6b6b;">🔴 Detected</td><td>EICAR Test String</td></tr>
  </tbody>
</table>

<h3>File Information</h3>
<ul>
  <li><strong>File Type:</strong> DOS executable</li>
  <li><strong>File Size:</strong> 68 bytes</li>
  <li><strong>First Seen:</strong> 2024-01-15</li>
  <li><strong>Last Scanned:</strong> 2024-12-16</li>
</ul>`
  },

  // ========== NETWORK INTERFACES ==========
  {
    patterns: ["network interface", "ip address", "mac address", "network adapter"],
    query: "Can you list all network interfaces and their IP/MAC addresses?",
    response: `<h2>Network Interfaces</h2>

<table>
  <thead>
    <tr>
      <th>Interface</th>
      <th>Type</th>
      <th>IP Address</th>
      <th>MAC Address</th>
      <th>Status</th>
    </tr>
  </thead>
  <tbody>
    <tr>
      <td><strong>en0</strong></td>
      <td>Ethernet</td>
      <td>192.168.1.100</td>
      <td>00:1A:2B:3C:4D:5E</td>
      <td style="color: #4caf50;">● Active</td>
    </tr>
    <tr>
      <td><strong>en1</strong></td>
      <td>Wi-Fi</td>
      <td>10.0.0.45</td>
      <td>AA:BB:CC:DD:EE:FF</td>
      <td style="color: #4caf50;">● Active</td>
    </tr>
    <tr>
      <td><strong>lo0</strong></td>
      <td>Loopback</td>
      <td>127.0.0.1</td>
      <td>00:00:00:00:00:00</td>
      <td style="color: #4caf50;">● Active</td>
    </tr>
    <tr>
      <td><strong>utun0</strong></td>
      <td>VPN Tunnel</td>
      <td>10.8.0.5</td>
      <td>N/A</td>
      <td style="color: #ff9800;">● Connected</td>
    </tr>
  </tbody>
</table>

<p><strong>Default Gateway:</strong> 192.168.1.1 &nbsp;&nbsp;|&nbsp;&nbsp; <strong>DNS Server:</strong> 8.8.8.8</p>`
  },

  // ========== TTP EXTRACTION ==========
  {
    patterns: ["ttp", "mitre", "attack", "technique", "tactics"],
    query: "Can you extract the TTPs of this file C:\\Users\\cooluser\\Downloads\\dolphine.exe",
    response: `<h2>MITRE ATT&CK TTPs Analysis</h2>

<h3>File: <code>dolphine.exe</code></h3>

<table>
  <thead>
    <tr>
      <th>Technique ID</th>
      <th>Technique Name</th>
      <th>Tactic</th>
      <th>Confidence</th>
    </tr>
  </thead>
  <tbody>
    <tr>
      <td><strong>T1055</strong></td>
      <td>Process Injection</td>
      <td>Defense Evasion, Privilege Escalation</td>
      <td style="color: #ff6b6b;">High (95%)</td>
    </tr>
    <tr>
      <td><strong>T1082</strong></td>
      <td>System Information Discovery</td>
      <td>Discovery</td>
      <td style="color: #ff9800;">Medium (78%)</td>
    </tr>
    <tr>
      <td><strong>T1012</strong></td>
      <td>Query Registry</td>
      <td>Discovery</td>
      <td style="color: #ff9800;">Medium (82%)</td>
    </tr>
    <tr>
      <td><strong>T1071</strong></td>
      <td>Application Layer Protocol</td>
      <td>Command and Control</td>
      <td style="color: #ff6b6b;">High (91%)</td>
    </tr>
    <tr>
      <td><strong>T1027</strong></td>
      <td>Obfuscated Files or Information</td>
      <td>Defense Evasion</td>
      <td style="color: #ff9800;">Medium (75%)</td>
    </tr>
  </tbody>
</table>

<h3>Summary</h3>
<p>The binary exhibits <strong>5 unique TTPs</strong> associated with sophisticated malware behavior, including process injection and C2 communication capabilities.</p>`
  },

  // ========== OPEN PORTS ==========
  {
    patterns: ["open port", "listening port", "tcp", "udp", "netstat"],
    query: "Can you display all open/listening TCP and UDP ports?",
    response: `<h2>Network Ports Analysis</h2>

<h3>TCP Listening Ports</h3>
<table>
  <thead>
    <tr>
      <th>Port</th>
      <th>Address</th>
      <th>Process</th>
      <th>PID</th>
      <th>State</th>
    </tr>
  </thead>
  <tbody>
    <tr><td>22</td><td>0.0.0.0</td><td>sshd</td><td>1234</td><td style="color: #4caf50;">LISTENING</td></tr>
    <tr><td>80</td><td>0.0.0.0</td><td>nginx</td><td>5678</td><td style="color: #4caf50;">LISTENING</td></tr>
    <tr><td>443</td><td>0.0.0.0</td><td>nginx</td><td>5679</td><td style="color: #4caf50;">LISTENING</td></tr>
    <tr><td>3000</td><td>127.0.0.1</td><td>node</td><td>9012</td><td style="color: #4caf50;">LISTENING</td></tr>
    <tr><td>5432</td><td>127.0.0.1</td><td>postgres</td><td>3456</td><td style="color: #4caf50;">LISTENING</td></tr>
    <tr><td>6379</td><td>127.0.0.1</td><td>redis-server</td><td>7890</td><td style="color: #4caf50;">LISTENING</td></tr>
  </tbody>
</table>

<h3>UDP Listening Ports</h3>
<table>
  <thead>
    <tr>
      <th>Port</th>
      <th>Address</th>
      <th>Process</th>
      <th>PID</th>
    </tr>
  </thead>
  <tbody>
    <tr><td>53</td><td>0.0.0.0</td><td>dnsmasq</td><td>2345</td></tr>
    <tr><td>5353</td><td>0.0.0.0</td><td>mDNSResponder</td><td>678</td></tr>
  </tbody>
</table>

<p><strong>Total Open Ports:</strong> TCP: 6 | UDP: 2</p>`
  },

  // ========== MALICIOUSNESS CHECK ==========
  {
    patterns: ["malicious", "is file malicious", "malware check", "benign", "verdict"],
    query: "is following file malicious dolphine.exe",
    response: `<h2>Security Verdict</h2>

<div style="background: rgba(244, 67, 54, 0.15); border: 1px solid rgba(244, 67, 54, 0.4); border-radius: 12px; padding: 20px; margin: 16px 0; text-align: center;">
  <h3 style="color: #ff6b6b; margin: 0 0 10px 0;">⚠️ MALICIOUS FILE DETECTED</h3>
  <p style="margin: 0; color: #ff8a80;">High confidence threat detection (Score: 87/100)</p>
</div>

<h3>Analysis Results</h3>

<table>
  <tbody>
    <tr><td><strong>File Name</strong></td><td>dolphine.exe</td></tr>
    <tr><td><strong>SHA256</strong></td><td><code>a3f5d7e8b9c1...</code></td></tr>
    <tr><td><strong>Threat Type</strong></td><td>Trojan.Generic.Dolphine</td></tr>
    <tr><td><strong>Risk Level</strong></td><td style="color: #ff6b6b;">🔴 Critical</td></tr>
    <tr><td><strong>First Seen</strong></td><td>2024-03-15</td></tr>
  </tbody>
</table>

<h3>Malicious Indicators</h3>
<ul>
  <li>✗ Packed executable with unusual entropy (7.8)</li>
  <li>✗ Contains suspicious API calls (CreateRemoteThread, VirtualAllocEx)</li>
  <li>✗ Network communication to known C2 server</li>
  <li>✗ Anti-debugging techniques detected</li>
  <li>✗ Attempts to modify system registry</li>
</ul>

<p><strong>Recommendation:</strong> Quarantine and delete this file immediately.</p>`
  },

  // ========== ABUSEIPDB ==========
  {
    patterns: ["abuseipdb", "ip reputation", "abuse report", "ip lookup"],
    query: "check IP 134.122.87.122 on abuseipdb",
    response: `<h2>AbuseIPDB Report</h2>

<h3>IP Address: <code>134.122.87.122</code></h3>

<div style="background: rgba(255, 152, 0, 0.15); border: 1px solid rgba(255, 152, 0, 0.4); border-radius: 12px; padding: 16px; margin: 16px 0;">
  <strong style="color: #ff9800;">⚠️ SUSPICIOUS</strong> - Abuse Confidence Score: <strong>67%</strong>
</div>

<table>
  <tbody>
    <tr><td><strong>ISP</strong></td><td>DigitalOcean, LLC</td></tr>
    <tr><td><strong>Country</strong></td><td>🇺🇸 United States</td></tr>
    <tr><td><strong>Usage Type</strong></td><td>Data Center/Web Hosting</td></tr>
    <tr><td><strong>Total Reports</strong></td><td>156 reports from 42 users</td></tr>
    <tr><td><strong>Last Reported</strong></td><td>2024-12-15</td></tr>
  </tbody>
</table>

<h3>Report Categories</h3>
<ul>
  <li>🔹 SSH Brute Force (45 reports)</li>
  <li>🔹 Port Scan (38 reports)</li>
  <li>🔹 Web App Attack (28 reports)</li>
  <li>🔹 Spam (23 reports)</li>
  <li>🔹 Other (22 reports)</li>
</ul>`
  },

  // ========== STRINGS EXTRACTION ==========
  {
    patterns: ["extract string", "strings from", "ip address", "url pattern"],
    query: "can you list all strings that looks like ip address in this file C:\\Users\\cooluser\\Downloads\\dolphine.exe",
    response: `<h2>String Extraction Results</h2>

<h3>IP Address Patterns Found</h3>

<table>
  <thead>
    <tr>
      <th>Offset</th>
      <th>IP Address</th>
      <th>Type</th>
      <th>Reputation</th>
    </tr>
  </thead>
  <tbody>
    <tr>
      <td>0x00045A2C</td>
      <td><code>192.168.1.1</code></td>
      <td>Private (RFC1918)</td>
      <td style="color: #4caf50;">✓ Safe</td>
    </tr>
    <tr>
      <td>0x00078F10</td>
      <td><code>45.33.32.156</code></td>
      <td>Public</td>
      <td style="color: #ff6b6b;">⚠ Known C2</td>
    </tr>
    <tr>
      <td>0x000A2B48</td>
      <td><code>185.220.101.34</code></td>
      <td>Public</td>
      <td style="color: #ff6b6b;">⚠ Tor Exit Node</td>
    </tr>
    <tr>
      <td>0x000C1E90</td>
      <td><code>127.0.0.1</code></td>
      <td>Localhost</td>
      <td style="color: #4caf50;">✓ Safe</td>
    </tr>
    <tr>
      <td>0x000D5F24</td>
      <td><code>8.8.8.8</code></td>
      <td>Public (Google DNS)</td>
      <td style="color: #4caf50;">✓ Safe</td>
    </tr>
  </tbody>
</table>

<p><strong>Total IP patterns found:</strong> 5 &nbsp;&nbsp;|&nbsp;&nbsp; <strong>Suspicious IPs:</strong> 2</p>`
  },

  // ========== PE HEADER ==========
  {
    patterns: ["pe header", "dos header", "nt header", "image header"],
    query: "print me the pe header of this file C:\\Users\\cooluser\\Downloads\\dolphine.exe",
    response: `<h2>PE Header Analysis</h2>

<h3>DOS Header (IMAGE_DOS_HEADER)</h3>
<table>
  <tbody>
    <tr><td><strong>e_magic</strong></td><td><code>0x5A4D</code> (MZ)</td></tr>
    <tr><td><strong>e_lfanew</strong></td><td><code>0x000000F0</code></td></tr>
  </tbody>
</table>

<h3>NT Headers (IMAGE_NT_HEADERS)</h3>
<table>
  <tbody>
    <tr><td><strong>Signature</strong></td><td><code>0x00004550</code> (PE)</td></tr>
    <tr><td><strong>Machine</strong></td><td><code>0x014C</code> (i386)</td></tr>
    <tr><td><strong>Number of Sections</strong></td><td>5</td></tr>
    <tr><td><strong>TimeDateStamp</strong></td><td>2024-01-15 14:32:18 UTC</td></tr>
    <tr><td><strong>Characteristics</strong></td><td>EXECUTABLE_IMAGE, 32BIT_MACHINE</td></tr>
  </tbody>
</table>

<h3>Optional Header</h3>
<table>
  <tbody>
    <tr><td><strong>Magic</strong></td><td><code>0x010B</code> (PE32)</td></tr>
    <tr><td><strong>Entry Point</strong></td><td><code>0x00012345</code></td></tr>
    <tr><td><strong>Image Base</strong></td><td><code>0x00400000</code></td></tr>
    <tr><td><strong>Section Alignment</strong></td><td><code>0x1000</code></td></tr>
    <tr><td><strong>Subsystem</strong></td><td>WINDOWS_GUI</td></tr>
  </tbody>
</table>`
  },

  // ========== ENVIRONMENT VARIABLES ==========
  {
    patterns: ["environment variable", "env var", "environment"],
    query: "can you list all environment variables in this machine",
    response: `<h2>Environment Variables</h2>

<table>
  <thead>
    <tr>
      <th>Variable</th>
      <th>Value</th>
    </tr>
  </thead>
  <tbody>
    <tr><td><strong>PATH</strong></td><td>/usr/local/bin:/usr/bin:/bin:/usr/sbin:/sbin</td></tr>
    <tr><td><strong>HOME</strong></td><td>/Users/researcher</td></tr>
    <tr><td><strong>USER</strong></td><td>researcher</td></tr>
    <tr><td><strong>SHELL</strong></td><td>/bin/zsh</td></tr>
    <tr><td><strong>LANG</strong></td><td>en_US.UTF-8</td></tr>
    <tr><td><strong>TERM</strong></td><td>xterm-256color</td></tr>
    <tr><td><strong>TMPDIR</strong></td><td>/var/folders/xyz/.../T/</td></tr>
    <tr><td><strong>PWD</strong></td><td>/Users/researcher/research</td></tr>
    <tr><td><strong>LOGNAME</strong></td><td>researcher</td></tr>
    <tr><td><strong>EDITOR</strong></td><td>vim</td></tr>
  </tbody>
</table>

<p><strong>Total Variables:</strong> 47</p>`
  },

  // ========== REGISTRY (Windows) ==========
  {
    patterns: ["registry", "hklm", "hkcu", "autorun", "startup"],
    query: "Can you list all subkeys under HKLM\\Software\\Microsoft\\Windows\\CurrentVersion\\Run?",
    response: `<h2>Registry Keys: Startup Programs</h2>

<h3>Path: <code>HKLM\\Software\\Microsoft\\Windows\\CurrentVersion\\Run</code></h3>

<table>
  <thead>
    <tr>
      <th>Name</th>
      <th>Type</th>
      <th>Value</th>
      <th>Status</th>
    </tr>
  </thead>
  <tbody>
    <tr>
      <td>SecurityHealth</td>
      <td>REG_EXPAND_SZ</td>
      <td>%ProgramFiles%\\Windows Defender\\MSASCuiL.exe</td>
      <td style="color: #4caf50;">✓ Trusted</td>
    </tr>
    <tr>
      <td>iTunesHelper</td>
      <td>REG_SZ</td>
      <td>"C:\\Program Files\\iTunes\\iTunesHelper.exe"</td>
      <td style="color: #4caf50;">✓ Signed</td>
    </tr>
    <tr>
      <td>VMware User Process</td>
      <td>REG_SZ</td>
      <td>"C:\\Program Files\\VMware\\VMware Tools\\vmtoolsd.exe"</td>
      <td style="color: #4caf50;">✓ Signed</td>
    </tr>
    <tr>
      <td>SuspiciousApp</td>
      <td>REG_SZ</td>
      <td>"C:\\Users\\Public\\svchost.exe"</td>
      <td style="color: #ff6b6b;">⚠ Suspicious</td>
    </tr>
  </tbody>
</table>

<p><em>⚠️ Note: SuspiciousApp is running from an unusual location and may require investigation.</em></p>`
  },

  // ========== DEFAULT FALLBACK ==========
  {
    patterns: [],
    query: "",
    response: `<h2>Query Processed</h2>

<p>I've analyzed your query and here's what I found:</p>

<div style="background: rgba(159, 136, 255, 0.1); border: 1px solid rgba(159, 136, 255, 0.3); border-radius: 8px; padding: 16px; margin: 12px 0;">
  <p style="margin: 0;">This is a <strong>demo environment</strong>. The full platform supports:</p>
</div>

<ul>
  <li>🔍 PE/ELF/Mach-O binary analysis</li>
  <li>🦠 Malware detection and TTP extraction</li>
  <li>🌐 VirusTotal, ThreatFox, MalwareBazaar and many more threat intelligence platforms integration</li>
  <li>📊 Process monitoring and analysis</li>
  <li>💻 System information collection</li>
  <li>🔐 Registry and memory analysis</li>
  <li>📡 Network interface enumeration</li>
  <li>🔍 Many more integrations and niche set of tasks</li>
</ul>

<p>Try one of the suggested queries to see the platform in action!</p>`
  }
];

/**
 * Demo chat sessions for the sidebar
 */
export const demoChatSessions = [
  { id: 1, name: "Malware Analysis Session", icon: "malware" },
  { id: 2, name: "System Investigation", icon: "system" },
  { id: 3, name: "Network Forensics", icon: "network" }
];

/**
 * Pre-populated chat histories for demo sessions
 */
export const demoChatHistories = {
  // Malware Analysis Session - empty by default, user can interact
  1: [],
  
  // System Investigation - pre-populated with system analysis conversation
  2: [
    {
      id: 'sys-1',
      role: 'user',
      content: 'can you tell me what disk partitions are there in this machine'
    },
    {
      id: 'sys-2',
      role: 'assistant',
      content: `<div class="response-section">
<h3>💾 Disk Partition Analysis</h3>
<table class="data-table">
<thead>
<tr><th>Device</th><th>Mountpoint</th><th>File System</th><th>Size</th><th>Used</th><th>Free</th></tr>
</thead>
<tbody>
<tr><td>C:</td><td>/</td><td>NTFS</td><td>500 GB</td><td>234 GB</td><td>266 GB</td></tr>
<tr><td>D:</td><td>/data</td><td>NTFS</td><td>1 TB</td><td>456 GB</td><td>544 GB</td></tr>
<tr><td>E:</td><td>/backup</td><td>exFAT</td><td>2 TB</td><td>1.2 TB</td><td>800 GB</td></tr>
</tbody>
</table>
<p><strong>Total Storage:</strong> 3.5 TB | <strong>Used:</strong> 1.89 TB (54%) | <strong>Available:</strong> 1.61 TB</p>
</div>`
    },
    {
      id: 'sys-3',
      role: 'user',
      content: 'list all running processes on this machine'
    },
    {
      id: 'sys-4',
      role: 'assistant',
      content: `<div class="response-section">
<h3>⚙️ Running Processes (Top 15 by CPU)</h3>
<table class="data-table">
<thead>
<tr><th>PID</th><th>Process Name</th><th>CPU %</th><th>Memory</th><th>Status</th></tr>
</thead>
<tbody>
<tr><td>4</td><td>System</td><td>0.1%</td><td>0.1 MB</td><td>Running</td></tr>
<tr><td>1234</td><td>chrome.exe</td><td>12.5%</td><td>1.2 GB</td><td>Running</td></tr>
<tr><td>5678</td><td>code.exe</td><td>8.3%</td><td>890 MB</td><td>Running</td></tr>
<tr><td>9012</td><td>explorer.exe</td><td>2.1%</td><td>156 MB</td><td>Running</td></tr>
<tr><td>3456</td><td>svchost.exe</td><td>1.8%</td><td>45 MB</td><td>Running</td></tr>
<tr><td>7890</td><td>MsMpEng.exe</td><td>1.5%</td><td>234 MB</td><td>Running</td></tr>
<tr><td>2345</td><td>SearchIndexer.exe</td><td>1.2%</td><td>78 MB</td><td>Running</td></tr>
<tr class="suspicious"><td>6789</td><td>dolphine.exe</td><td>15.2%</td><td>456 MB</td><td>⚠️ Suspicious</td></tr>
</tbody>
</table>
<p><strong>Total Processes:</strong> 156 | <strong>⚠️ Suspicious:</strong> 1 detected</p>
</div>`
    },
    {
      id: 'sys-5',
      role: 'user',
      content: 'Can you list all subkeys under HKLM\\Software\\Microsoft\\Windows\\CurrentVersion\\Run?'
    },
    {
      id: 'sys-6',
      role: 'assistant',
      content: `<div class="response-section">
<h3>🔑 Registry: Auto-Run Programs</h3>
<p><code>HKLM\\Software\\Microsoft\\Windows\\CurrentVersion\\Run</code></p>
<table class="data-table">
<thead>
<tr><th>Name</th><th>Value</th><th>Status</th></tr>
</thead>
<tbody>
<tr><td>SecurityHealth</td><td>%ProgramFiles%\\Windows Defender\\MSASCuiL.exe</td><td>✅ Verified</td></tr>
<tr><td>iTunesHelper</td><td>"C:\\Program Files\\iTunes\\iTunesHelper.exe"</td><td>✅ Verified</td></tr>
<tr><td>Discord</td><td>"C:\\Users\\user\\AppData\\Local\\Discord\\Update.exe" --processStart Discord.exe</td><td>✅ Verified</td></tr>
<tr class="suspicious"><td>WinUpdate32</td><td>"C:\\Users\\user\\AppData\\Roaming\\svchost.exe" -hidden</td><td>⚠️ Suspicious</td></tr>
</tbody>
</table>
<div class="alert warning">
<strong>⚠️ Warning:</strong> Suspicious entry detected! "WinUpdate32" appears to be masquerading as a system process.
</div>
</div>`
    }
  ],
  
  // Network Forensics - pre-populated with network analysis conversation
  3: [
    {
      id: 'net-1',
      role: 'user',
      content: 'Can you list all network interfaces and their IP/MAC addresses?'
    },
    {
      id: 'net-2',
      role: 'assistant',
      content: `<div class="response-section">
<h3>🌐 Network Interfaces</h3>
<table class="data-table">
<thead>
<tr><th>Interface</th><th>IP Address</th><th>MAC Address</th><th>Status</th><th>Speed</th></tr>
</thead>
<tbody>
<tr><td>Ethernet</td><td>192.168.1.105</td><td>00:1A:2B:3C:4D:5E</td><td>🟢 Connected</td><td>1 Gbps</td></tr>
<tr><td>Wi-Fi</td><td>192.168.1.150</td><td>F1:E2:D3:C4:B5:A6</td><td>🟢 Connected</td><td>866 Mbps</td></tr>
<tr><td>Loopback</td><td>127.0.0.1</td><td>N/A</td><td>🟢 Active</td><td>N/A</td></tr>
<tr><td>VPN Adapter</td><td>10.8.0.15</td><td>00:FF:AA:BB:CC:DD</td><td>🔴 Disconnected</td><td>N/A</td></tr>
</tbody>
</table>
<p><strong>Default Gateway:</strong> 192.168.1.1 | <strong>DNS:</strong> 8.8.8.8, 8.8.4.4</p>
</div>`
    },
    {
      id: 'net-3',
      role: 'user',
      content: 'Can you display all open/listening TCP and UDP ports?'
    },
    {
      id: 'net-4',
      role: 'assistant',
      content: `<div class="response-section">
<h3>🔌 Open Ports Analysis</h3>
<h4>TCP Ports</h4>
<table class="data-table">
<thead>
<tr><th>Port</th><th>Process</th><th>PID</th><th>State</th><th>Remote Address</th></tr>
</thead>
<tbody>
<tr><td>80</td><td>httpd.exe</td><td>2345</td><td>LISTENING</td><td>0.0.0.0:*</td></tr>
<tr><td>443</td><td>httpd.exe</td><td>2345</td><td>LISTENING</td><td>0.0.0.0:*</td></tr>
<tr><td>3389</td><td>TermService</td><td>1456</td><td>LISTENING</td><td>0.0.0.0:*</td></tr>
<tr><td>445</td><td>System</td><td>4</td><td>LISTENING</td><td>0.0.0.0:*</td></tr>
<tr class="suspicious"><td>4444</td><td>dolphine.exe</td><td>6789</td><td>ESTABLISHED</td><td>134.122.87.122:443</td></tr>
<tr><td>5432</td><td>postgres.exe</td><td>3456</td><td>LISTENING</td><td>127.0.0.1:*</td></tr>
</tbody>
</table>
<h4>UDP Ports</h4>
<table class="data-table">
<thead>
<tr><th>Port</th><th>Process</th><th>PID</th><th>State</th></tr>
</thead>
<tbody>
<tr><td>53</td><td>svchost.exe</td><td>1234</td><td>LISTENING</td></tr>
<tr><td>137</td><td>System</td><td>4</td><td>LISTENING</td></tr>
<tr><td>138</td><td>System</td><td>4</td><td>LISTENING</td></tr>
</tbody>
</table>
<div class="alert danger">
<strong>🚨 Alert:</strong> Suspicious outbound connection detected on port 4444 to 134.122.87.122 (known malicious IP)
</div>
</div>`
    },
    {
      id: 'net-5',
      role: 'user',
      content: 'check IP 134.122.87.122 on abuseipdb'
    },
    {
      id: 'net-6',
      role: 'assistant',
      content: `<div class="response-section">
<h3>🔍 AbuseIPDB Analysis</h3>
<div class="ip-header danger">
<span class="ip-address">134.122.87.122</span>
<span class="threat-score critical">Abuse Score: 100%</span>
</div>

<table class="data-table">
<thead>
<tr><th>Property</th><th>Value</th></tr>
</thead>
<tbody>
<tr><td>ISP</td><td>DigitalOcean, LLC</td></tr>
<tr><td>Country</td><td>🇺🇸 United States</td></tr>
<tr><td>Domain</td><td>digitalocean.com</td></tr>
<tr><td>Usage Type</td><td>Data Center/Web Hosting</td></tr>
<tr><td>Total Reports</td><td>2,847</td></tr>
<tr><td>Last Reported</td><td>2 hours ago</td></tr>
</tbody>
</table>

<h4>⚠️ Recent Attack Categories</h4>
<ul class="attack-list">
<li>🔴 <strong>Port Scan</strong> - 45% of reports</li>
<li>🔴 <strong>Brute Force (SSH)</strong> - 28% of reports</li>
<li>🔴 <strong>C2 Server (Command & Control)</strong> - 15% of reports</li>
<li>🟡 <strong>Web Attack</strong> - 12% of reports</li>
</ul>

<div class="alert danger">
<strong>🚨 HIGH RISK:</strong> This IP is associated with known malware command & control infrastructure. 
Immediate action recommended: Block this IP at the firewall level.
</div>
</div>`
    }
  ]
};

/**
 * Demo suggestions for the demo portal
 */
export const demoSuggestions = [
  {
    text: "can you tell me what disk partitions are there in this machine",
    category: "system"
  },
  {
    text: "list all running processes on this machine",
    category: "process"
  },
  {
    text: "scan this file hash on VirusTotal: 44d88612fea8a8f36de82e1278abb02f",
    category: "virustotal"
  },
  {
    text: "Can you list all network interfaces and their IP/MAC addresses?",
    category: "network"
  },
  {
    text: "Can you extract the TTPs of this file C:\\Users\\cooluser\\Downloads\\dolphine.exe",
    category: "ttp"
  },
  {
    text: "is following file malicious dolphine.exe",
    category: "verdict"
  },
  {
    text: "print me the pe header of this file C:\\Users\\cooluser\\Downloads\\dolphine.exe",
    category: "pe"
  },
  {
    text: "check IP 134.122.87.122 on abuseipdb",
    category: "abuseipdb"
  },
  {
    text: "Can you display all open/listening TCP and UDP ports?",
    category: "network"
  },
  {
    text: "Can you list all subkeys under HKLM\\Software\\Microsoft\\Windows\\CurrentVersion\\Run?",
    category: "registry"
  }
];

/**
 * Find the best matching response for a query
 * @param {string} query - The user's query
 * @returns {object} - The matching response object
 */
export const findDemoResponse = (query) => {
  const normalizedQuery = query.toLowerCase().trim();
  
  // Try to find exact or pattern match
  for (const response of demoResponses) {
    // Check if query matches any pattern
    if (response.patterns.some(pattern => normalizedQuery.includes(pattern.toLowerCase()))) {
      return response;
    }
    
    // Check if query is similar to stored query
    if (response.query && normalizedQuery.includes(response.query.toLowerCase().substring(0, 20))) {
      return response;
    }
  }
  
  // Return default fallback
  return demoResponses[demoResponses.length - 1];
};

export default demoResponses;

